import i18n from '@/utils/i18n';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { agentFetch } from '@/experiences/sales-portal/domain/superintelligence/blocs/services/agent-fetch';
import { AgentFrontendTools } from '@/experiences/sales-portal/domain/superintelligence/components/agent-frontend-tools';
import { AssistantRuntime } from '@assistant-ui/core';
import { AssistantRuntimeProvider } from '@assistant-ui/core/react';
import {
  cancelThreadLists,
  invalidateThreadLists,
  updateCachedThread,
  useThreadListQuery,
} from '@/experiences/sales-portal/domain/superintelligence/blocs/queries/use-thread-list-query';
import {
  deleteThread,
  getThread,
  patchThread,
  ThreadDetail,
  ThreadStatus,
  ThreadSummary,
} from '@/experiences/sales-portal/domain/superintelligence/blocs/services/thread-api';
import { HttpAgent } from '@ag-ui/client';
import { HttpError } from '@platform/http/calyx-client';
import {
  hydrateThreadMessages,
  toMessageRepository,
} from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/thread-hydration';
import { superintelligenceEndpoint } from '@/experiences/sales-portal/domain/superintelligence/constants';
import { threadItemCustom } from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/thread-item-details';
import { threadTitleFrom } from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/thread-title';
import {
  useAgUiRuntime,
  UseAgUiThreadListAdapter,
} from '@assistant-ui/react-ag-ui';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

const RUN_IN_FLIGHT_POLL_INTERVAL = 4000;

const newThreadId = () => `thread-${uuidv4()}`;

interface SuperintelligenceContextValue {
  dismissError: () => void;
  error: string | null;
  isLoadingThread: boolean;
  refreshThread: () => void;
  runInFlight: boolean;
}

const SuperintelligenceContext =
  createContext<SuperintelligenceContextValue | null>(null);

const HTTP_ERROR_COPY: Record<string, string> = {
  '401': 'sales_portal.superintelligence.session_expired',
  '403': 'sales_portal.superintelligence.unavailable',
  '404': 'sales_portal.superintelligence.thread_not_found',
  '409': 'sales_portal.superintelligence.thread_busy',
};

const errorCopy = (error: unknown, fallback: string): string => {
  const status =
    error instanceof HttpError
      ? String(error.status)
      : error instanceof Error
        ? /^HTTP (\d{3})\b/.exec(error.message)?.[1]
        : undefined;
  const key = status ? HTTP_ERROR_COPY[status] : undefined;
  return key ? i18n.t(key) : fallback;
};

const draftSummary = (threadId: string, title: string | undefined) => {
  const now = new Date().toISOString();
  return {
    createdAt: now,
    lastRun: null,
    status: 'regular' as const,
    threadId,
    title: title ?? null,
    updatedAt: now,
  };
};

const summaryFromDetail = (detail: ThreadDetail): ThreadSummary => ({
  createdAt: detail.createdAt,
  lastRun: null,
  status: detail.status,
  threadId: detail.threadId,
  title: detail.title,
  updatedAt: detail.updatedAt,
});

const withOpenThread = (
  threads: readonly ThreadSummary[],
  status: ThreadStatus,
  open: ThreadSummary | null,
): ThreadSummary[] => {
  const listed = threads.filter((thread) => thread.status === status);
  if (
    !open ||
    open.status !== status ||
    listed.some((thread) => thread.threadId === open.threadId)
  ) {
    return listed;
  }
  const index = listed.findIndex((thread) => thread.updatedAt < open.updatedAt);
  return index === -1
    ? [...listed, open]
    : [...listed.slice(0, index), open, ...listed.slice(index)];
};

export const useSuperintelligenceContext = () => {
  const value = useContext(SuperintelligenceContext);
  if (!value) {
    throw new Error(
      'useSuperintelligenceContext requires SuperintelligenceRuntimeProvider',
    );
  }
  return value;
};

interface SuperintelligenceRuntimeProviderProps {
  initialThreadId?: string;
  onThreadIdChange?: (threadId: string | undefined) => void;
}

export const SuperintelligenceRuntimeProvider: React.FC<
  React.PropsWithChildren<SuperintelligenceRuntimeProviderProps>
> = ({ children, initialThreadId, onThreadIdChange }) => {
  const queryClient = useQueryClient();
  const { showErrorToast } = useExperienceToasts();
  const [error, setError] = useState<string | null>(null);
  const [threadId, setThreadId] = useState(newThreadId);
  const [fetchedThread, setFetchedThread] = useState<ThreadSummary | null>(
    null,
  );
  const [runInFlight, setRunInFlight] = useState<string | null>(null);
  const [loadingThreadId, setLoadingThreadId] = useState<string | null>(
    initialThreadId ?? null,
  );
  const [draftThreads, setDraftThreads] = useState<
    ReadonlyMap<string, ThreadSummary>
  >(() => new Map());
  // The runtime hydrates the next thread before React re-renders with its id, so the subscriber reads the id
  // from this ref.
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  const openThread =
    fetchedThread?.threadId === threadId ? fetchedThread : null;
  const startedThreadsRef = useRef(new Set<string>());
  const runtimeRef = useRef<AssistantRuntime | null>(null);
  const switchAbortRef = useRef<AbortController | null>(null);

  const regularThreads = useThreadListQuery('regular');
  const archivedThreads = useThreadListQuery('archived', false);

  const threadSummaries = useMemo(() => {
    const summaries = new Map(
      [...regularThreads.threads, ...archivedThreads.threads].map((thread) => [
        thread.threadId,
        thread,
      ]),
    );
    if (openThread && !summaries.has(openThread.threadId)) {
      summaries.set(openThread.threadId, openThread);
    }
    return summaries;
  }, [archivedThreads.threads, openThread, regularThreads.threads]);
  const threadSummariesRef = useRef(threadSummaries);
  threadSummariesRef.current = threadSummaries;

  useEffect(() => {
    setDraftThreads((drafts) => {
      const listed = [...drafts.keys()].filter((id) => threadSummaries.has(id));
      if (listed.length === 0) {
        return drafts;
      }
      const next = new Map(drafts);
      listed.forEach((id) => next.delete(id));
      return next;
    });
  }, [threadSummaries]);

  const agent = useMemo(
    () =>
      new HttpAgent({
        fetch: agentFetch,
        headers: { Accept: 'text/event-stream' },
        threadId,
        url: superintelligenceEndpoint(),
      }),
    [threadId],
  );

  const onError = useCallback((e: Error) => {
    setError(errorCopy(e, e.message));
  }, []);
  const dismissError = useCallback(() => {
    setError(null);
  }, []);

  const switchToFreshThread = useCallback(() => {
    const id = newThreadId();
    threadIdRef.current = id;
    setThreadId(id);
    setFetchedThread(null);
    setRunInFlight(null);
    onThreadIdChange?.(undefined);
  }, [onThreadIdChange]);

  const applyThreadDetail = useCallback((detail: ThreadDetail) => {
    setFetchedThread(summaryFromDetail(detail));
    setRunInFlight(detail.runInFlight);
    const messages = hydrateThreadMessages(detail);
    if (messages.length > 0) {
      startedThreadsRef.current.add(detail.threadId);
    }
    return messages;
  }, []);

  const finishLoading = useCallback((id: string) => {
    setLoadingThreadId((current) => (current === id ? null : current));
  }, []);

  const mutateThread = useCallback(
    async (
      id: string,
      call: () => Promise<unknown>,
      optimistic: (thread: ThreadSummary) => ThreadSummary | null,
    ) => {
      await cancelThreadLists(queryClient);
      updateCachedThread(queryClient, id, optimistic);
      try {
        await call();
        return true;
      } catch {
        showErrorToast(
          i18n.t('sales_portal.superintelligence.thread_action_failed'),
        );
        return false;
      } finally {
        void invalidateThreadLists(queryClient);
      }
    },
    [queryClient, showErrorToast],
  );

  const regularItems = useMemo(() => {
    const items = withOpenThread(
      regularThreads.threads,
      'regular',
      openThread,
    ).map((thread) => ({
      custom: threadItemCustom(thread),
      id: thread.threadId,
      status: 'regular' as const,
      title: thread.title ?? undefined,
    }));
    if (threadSummaries.has(threadId)) {
      return items;
    }
    const draft = draftThreads.get(threadId);
    const current = {
      custom: { lastRun: null, persisted: false, updatedAt: draft?.updatedAt },
      id: threadId,
      status: 'regular' as const,
      title: draft?.title ?? undefined,
    };
    return [current, ...items];
  }, [
    draftThreads,
    openThread,
    regularThreads.threads,
    threadId,
    threadSummaries,
  ]);

  const archivedItems = useMemo(
    () =>
      withOpenThread(archivedThreads.threads, 'archived', openThread).map(
        (thread) => ({
          custom: threadItemCustom(thread),
          id: thread.threadId,
          status: 'archived' as const,
          title: thread.title ?? undefined,
        }),
      ),
    [archivedThreads.threads, openThread],
  );

  const threadList = useMemo<UseAgUiThreadListAdapter>(
    () => ({
      archivedThreads: archivedItems,
      isLoading: regularThreads.isLoading,
      onArchive: async (id) => {
        const ok = await mutateThread(
          id,
          () => patchThread(id, { status: 'archived' }),
          (thread) => ({ ...thread, status: 'archived' }),
        );
        if (ok && id === threadIdRef.current) {
          await runtimeRef.current?.threads.switchToNewThread();
        }
      },
      onDelete: async (id) => {
        const ok = await mutateThread(
          id,
          () => deleteThread(id),
          () => null,
        );
        if (ok && id === threadIdRef.current) {
          await runtimeRef.current?.threads.switchToNewThread();
        }
      },
      onRename: async (id, title) => {
        const ok = await mutateThread(
          id,
          () => patchThread(id, { title }),
          (thread) => ({ ...thread, title }),
        );
        if (ok && id === threadIdRef.current) {
          setFetchedThread((thread) => thread && { ...thread, title });
        }
      },
      onSwitchToNewThread: async () => {
        if (switchAbortRef.current) {
          switchAbortRef.current.abort();
          switchAbortRef.current = null;
          setLoadingThreadId(null);
        }
        if (!startedThreadsRef.current.has(threadIdRef.current)) {
          return;
        }
        setError(null);
        switchToFreshThread();
      },
      onSwitchToThread: async (id) => {
        switchAbortRef.current?.abort();
        const controller = new AbortController();
        switchAbortRef.current = controller;
        setLoadingThreadId(id);
        try {
          const detail = await getThread(id, controller.signal);
          if (controller.signal.aborted) {
            return { messages: [] };
          }
          threadIdRef.current = id;
          setThreadId(id);
          setError(null);
          onThreadIdChange?.(id);
          return { messages: applyThreadDetail(detail), state: detail.state };
        } catch (e) {
          if (controller.signal.aborted) {
            return { messages: [] };
          }
          setError(
            errorCopy(
              e,
              i18n.t('sales_portal.superintelligence.thread_not_found'),
            ),
          );
          void invalidateThreadLists(queryClient);
          switchToFreshThread();
          return { messages: [] };
        } finally {
          if (switchAbortRef.current === controller) {
            switchAbortRef.current = null;
            finishLoading(id);
          }
        }
      },
      onUnarchive: async (id) => {
        const ok = await mutateThread(
          id,
          () => patchThread(id, { status: 'regular' }),
          (thread) => ({ ...thread, status: 'regular' }),
        );
        if (ok && id === threadIdRef.current) {
          setFetchedThread(
            (thread) => thread && { ...thread, status: 'regular' },
          );
        }
      },
      threadId,
      threads: regularItems,
    }),
    [
      applyThreadDetail,
      archivedItems,
      finishLoading,
      mutateThread,
      onThreadIdChange,
      queryClient,
      regularItems,
      regularThreads.isLoading,
      switchToFreshThread,
      threadId,
    ],
  );

  const runtime = useAgUiRuntime({
    adapters: { threadList },
    agent,
    onError,
    resumeTranscript: 'appended',
  });
  runtimeRef.current = runtime;

  useEffect(() => {
    let wasRunning = runtime.thread.getState().isRunning;
    return runtime.thread.subscribe(() => {
      const { isRunning, messages } = runtime.thread.getState();
      const id = threadIdRef.current;
      let changed = isRunning !== wasRunning;
      wasRunning = isRunning;
      if (messages.length > 0 && !startedThreadsRef.current.has(id)) {
        startedThreadsRef.current.add(id);
        onThreadIdChange?.(id);
        changed = true;
        if (!threadSummariesRef.current.has(id)) {
          setDraftThreads((drafts) =>
            drafts.has(id)
              ? drafts
              : new Map(drafts).set(
                  id,
                  draftSummary(id, threadTitleFrom(messages)),
                ),
          );
        }
      }
      if (changed) {
        void invalidateThreadLists(queryClient);
      }
    });
  }, [onThreadIdChange, queryClient, runtime]);

  const initialThreadIdRef = useRef(initialThreadId);
  useEffect(() => {
    const id = initialThreadIdRef.current;
    initialThreadIdRef.current = undefined;
    if (id) {
      void runtime.threads.switchToThread(id);
    }
  }, [runtime]);

  const { data: inFlightDetail, refetch: refetchInFlight } = useQuery({
    enabled: runInFlight !== null,
    gcTime: 0,
    queryFn: ({ signal }) => getThread(threadId, signal),
    queryKey: ['superintelligence', 'thread', threadId, 'in-flight'],
    refetchInterval: RUN_IN_FLIGHT_POLL_INTERVAL,
    staleTime: 0,
  });
  useEffect(() => {
    if (
      !inFlightDetail ||
      inFlightDetail.threadId !== threadIdRef.current ||
      inFlightDetail.runInFlight !== null
    ) {
      return;
    }
    runtime.thread.import(
      toMessageRepository(applyThreadDetail(inFlightDetail)),
    );
    void invalidateThreadLists(queryClient);
  }, [applyThreadDetail, inFlightDetail, queryClient, runtime]);
  const refreshThread = useCallback(() => {
    void refetchInFlight();
  }, [refetchInFlight]);

  const value = useMemo<SuperintelligenceContextValue>(
    () => ({
      dismissError,
      error,
      isLoadingThread: loadingThreadId !== null,
      refreshThread,
      runInFlight: runInFlight !== null,
    }),
    [dismissError, error, loadingThreadId, refreshThread, runInFlight],
  );

  return (
    <SuperintelligenceContext.Provider value={value}>
      <AssistantRuntimeProvider runtime={runtime}>
        <AgentFrontendTools />
        {children}
      </AssistantRuntimeProvider>
    </SuperintelligenceContext.Provider>
  );
};
